import React, { useState, useEffect } from "react";
import { db, auth, app } from "../../firebase-config";
import { collection, getDocs, addDoc, getFirestore, query, where, initializeFirestore, onSnapshot, doc, orderBy, limit, updateDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { Card, CardContent } from "../../components/ui/Card";
import Sidebar from "../../components/ui/Sidebar";
import { Bar } from "react-chartjs-2";
import "../../style.css";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Switch, FormControlLabel, IconButton, Select, MenuItem, FormControl, InputLabel, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";

ChartJS.register(BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [recentlyCreatedUsers, setRecentlyCreatedUsers] = useState([]);
  const [disabledAccounts, setDisabledAccounts] = useState([]);
  const [failedLogins, setFailedLogins] = useState([]);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [resendStatus, setResendStatus] = useState({});
  const [recentImports, setRecentImports] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [loginTrends, setLoginTrends] = useState([]);
  const [recentErrors, setRecentErrors] = useState([]);
  const [selectedRange, setSelectedRange] = useState([]);
  const [adminActions, setAdminActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mfaSummary, setMfaSummary] = useState({ enabled: 0, disabled: 0 });
  const [enableRealtimeMfa, setEnableRealtimeMfa] = useState(false);
  const [enableRealtimeAdminActions, setEnableRealtimeAdminActions] = useState(false);
  const [enableRealtimeErrors, setEnableRealtimeErrors] = useState(false);
  const [unsubscribeErrors, setUnsubscribeErrors] = useState(null);
  const [hiddenCards, setHiddenCards] = useState(
    JSON.parse(localStorage.getItem("hiddenCards")) || []
  );

  useEffect(() => {
    setLoading(true); // Start loading
  
    let unsubscribeAdminActions = null;
    let unsubscribeMfaStatus = null;
  
    async function loadData() {
      try {
        await Promise.all([
          fetchUsers(),
          fetchImports(),
          fetchRecentErrors(enableRealtimeErrors),
          fetchLoginTrends(selectedRange),
          fetchAdminActions(),
          fetchMfaSummary(),
          fetchActiveSessions(),
          fetchFailedLogins(),
        ]);
  
        // Real-time listeners
        if (enableRealtimeAdminActions) {
          const orgId = sessionStorage.getItem("organizationId");
          if (orgId) {
            unsubscribeAdminActions = onSnapshot(
              query(
                collection(db, "audits"),
                where("organizationId", "==", orgId),
                orderBy("timestamp", "desc"),
                limit(10)
              ),
              (snapshot) => {
                const actionsData = snapshot.docs.map((doc) => ({
                  id: doc.id,
                  ...doc.data(),
                }));
                console.log("Real-Time Admin Actions Update:", actionsData);
                setAdminActions(actionsData);
              }
            );
          }
        }
  
        if (enableRealtimeMfa) {
          const orgId = sessionStorage.getItem("organizationId");
          if (orgId) {
            unsubscribeMfaStatus = onSnapshot(
              query(collection(db, "users"), where("organizationId", "==", orgId)),
              (snapshot) => {
                let mfaEnabledCount = 0;
                let mfaDisabledCount = 0;
  
                snapshot.docs.forEach((doc) => {
                  if (doc.data().mfaEnabled) {
                    mfaEnabledCount++;
                  } else {
                    mfaDisabledCount++;
                  }
                });
  
                console.log("Real-Time MFA Status Update:", { enabled: mfaEnabledCount, disabled: mfaDisabledCount });
                setMfaSummary({ enabled: mfaEnabledCount, disabled: mfaDisabledCount });
              }
            );
          }
        }
  
        // Mark loading as done
        setLoading(false);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
        setLoading(false);
      }
    }
  
    loadData();
  
    return () => {
      if (unsubscribeAdminActions) {
        unsubscribeAdminActions();
      }
      if (unsubscribeMfaStatus) {
        unsubscribeMfaStatus();
      }
    };
  }, [enableRealtimeErrors, selectedRange, enableRealtimeAdminActions, enableRealtimeMfa]);

  const handleRefreshLoginTrends = async () => {
    console.log("Refreshing login trends...");
    await fetchLoginTrends();
  };
  
  const handleRefreshPendingVerifications = () => {
    console.log("Refreshing pending verifications...");
    fetchUsers();
  };

  const handleRefreshTotalUsers = () => {
    console.log("Refreshing Total Users...");
    fetchUsers();
  };

  const handleRefreshRecentlyCreatedUsers = () => {
    console.log("Refreshing Recently Created Users");
    fetchUsers();
  }

  const handleRefreshDisabledAccounts = () => {
    console.log("Refreshing Disabled Acoounts");
    fetchUsers();
  }

  const handleRefreshRecentUserImports = () => {
    console.log("Refreshing Recent User Imports");
    fetchImports();
  }

  const handleRefreshRecentErrors = () => {
    console.log("Refreshing Recent Errors");
    fetchRecentErrors();
  }

  /** Extend Users Sessions */
  const extendUserSession = async (userId) => {
    const sessionRef = doc(db, "activeSessions", userId);
  
    const newExpirationTime = new Date();
    newExpirationTime.setHours(newExpirationTime.getHours() + 6); // Extend session
  
    await updateDoc(sessionRef, {
      expirationTime: newExpirationTime,
    });
  };

  /** Fetch User records from Firebase Auth  **/
  const fetchUsers = async () => {
  const auth = getAuth();

  onAuthStateChanged(auth, async (user) => {
    if (!user) {
      console.warn("No user is currently logged in. Skipping fetchUsers.");
      return;
    }

    try {
      // Force refresh the ID token to get the latest custom claims.
      const idToken = await user.getIdToken(true);
      console.log("ID Token:", idToken); // For debugging purposes.

      // Define the Cloud Function URL.
      const functionUrl = 'https://getfirebaseusers-774073159618.us-central1.run.app';

      // Make the HTTP request to the Cloud Function.
      const response = await fetch(functionUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Error fetching users: ${response.statusText}`);
      }

      const users = await response.json();
         // Sort users by creation date in descending order
      const sortedUsers = users.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));


      // Update your state or UI with the retrieved users.
      setUsers(users);
      setPendingVerifications(users.filter((user) => !user.emailVerified));
      setRecentlyCreatedUsers(sortedUsers.slice(0, 5)); // Last 5 created users.
      
      // Fetch disabled accounts from Firestore
      const disabledFirestoreUsers = await fetchDisabledUsersFromFirestore();

      // Combine Firebase Auth and Firestore data for disabled accounts
      const combinedDisabledAccounts = users
        .filter((user) => user.disabled)
        .map((authDisabledUser) => {
          const firestoreDisabledUser = disabledFirestoreUsers.find(
            (fsUser) => fsUser.id === authDisabledUser.uid
          );
          return {
            ...authDisabledUser,
            disabledAt: firestoreDisabledUser?.disabledAt,
          };
        });

      setDisabledAccounts(combinedDisabledAccounts);

    } catch (error) {
      console.error("Error fetching users:", error);
    }
  });
};
    
      const fetchDisabledUsersFromFirestore = async () => {
        const q = query(collection(db, 'users'), where('disabledAt', '!=', null));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      };

      const handleResendVerification = async (email) => {
        try {
          if (!auth.currentUser) {
            setResendStatus((prev) => ({
              ...prev,
              [email]: "You must be logged in to resend verification emails.",
            }));
            return;
          }
      
          // Get the user's ID token
          const idToken = await auth.currentUser.getIdToken(true);
      
          // Make the HTTP POST request to the Cloud Function
          const response = await fetch(
            `https://sendemailverification-774073159618.us-central1.run.app`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${idToken}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ email }),
            }
          );
      
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || "Failed to send verification email");
          }
      
          const result = await response.json();
      
          // Update resendStatus for this email only
          setResendStatus((prev) => ({
            ...prev,
            [email]: result.message || "Verification email sent.",
          }));
        } catch (error) {
          setResendStatus((prev) => ({
            ...prev,
            [email]: `Error: ${error.message}`,
          }));
        }
      };

    const ResendVerificationButton = ({ email }) => {
      const functions = getFunctions();
      const auth = getAuth();
      return (
        <Button
          variant="contained"
          color="primary"
          sx={{ 
            fontSize: '10px',
            padding: '4px 8px'
          }}
          onClick={() => handleResendVerification(email)} // Call handleResendVerification
        >
          Resend Email
        </Button>
      );
    };

  const fetchImports = async () => {
    const importsCollection = await getDocs(collection(db, "audits"));
    const importData = importsCollection.docs.map((doc) => doc.data());
    console.log("User Imports: ", importsCollection);
    console.log("Import Data: ", importData);


    // Only keep the latest 5 imports
    setRecentImports(importData.slice(0, 5));
  };

/** Fetch Recent Admin Actions from Firestore **/
const fetchAdminActions = async () => {
  console.log("Fetching recent admin actions...");

  try {
    const orgId = sessionStorage.getItem("organizationId"); 
    if (!orgId) {
      console.warn("No organization ID found.");
      return;
    }

    const actionsQuery = query(
      collection(db, "audits"),
      where("organizationId", "==", orgId), // Filter by organization
      orderBy("timestamp", "desc"), // Sort by most recent
      limit(10) // Limit to the last 10 actions
    );

    const snapshot = await getDocs(actionsQuery);
    const actionsData = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    console.log("Recent Admin Actions:", actionsData);
    setAdminActions(actionsData);
  } catch (error) {
    console.error("Error fetching admin actions:", error);
  }
};
  /** Fetch Failed Logins from Firestore **/
  const fetchFailedLogins = () => {
    console.log("Listening for real-time failed login attempts...");
  
    const orgId = sessionStorage.getItem("organizationId");
    if (!orgId) {
      console.warn("⚠️ No organization ID found for admin.");
      return;
    }
  
    const unsubscribe = onSnapshot(
      query(
        collection(db, "failedLogins"),
        where("organizationId", "==", orgId),
        orderBy("timestamp", "desc")
      ),
      (snapshot) => {
        const failedLoginsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
  
        console.log("Updated Failed Logins:", failedLoginsData);
        setFailedLogins(failedLoginsData);
      },
      (error) => {
        console.error("Error listening for failed logins:", error);
      }
    );
  
    return unsubscribe; // Cleanup listener on unmount
  };

/** Fetch & Categorize System Errors **/
const fetchRecentErrors = async (realtime = false) => {
  try {
    console.log("Fetching system logs...");
    const orgId = sessionStorage.getItem("organizationId");
    if (!orgId) {
      console.warn("No organization ID found. Skipping error fetch.");
      return;
    }

    if (!realtime) {
      const errorsSnapshot = await getDocs(
        query(
          collection(db, "systemLogs"),
          where("organizationId", "==", orgId),
          orderBy("timestamp", "desc")
        )
      );
      setRecentErrors(errorsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    }
  } catch (error) {
    console.error("Error fetching system logs:", error);
  }
};

  /** Download System Logs **/
  const orgId = sessionStorage.getItem("organizationId");
  const handleDownloadErrors = async () => {
    try {
      console.log("Downloading full system error logs...");
      const errorsSnapshot = await getDocs(query(collection(db, "systemLogs"),
      where("organizationId", "==", orgId),
      orderBy("timestamp", "desc")));
      const errorData = errorsSnapshot.docs.map((doc) => ({
        timestamp: new Date(doc.data().timestamp.seconds * 1000).toLocaleString(),
        category: doc.data().category || "General Error",
        message: doc.data().message,
        severity: doc.data().severity || "unknown",
      }));
  
      if (errorData.length === 0) {
        alert("No error logs found.");
        return;
      }
  
      // Convert data to CSV
      const csvContent = [
        ["Timestamp", "Category", "Message", "Severity"],
        ...errorData.map((error) => [error.timestamp, error.category, error.message, error.severity]),
      ]
        .map((row) => row.join(","))
        .join("\n");
  
      // Create and trigger CSV download
      const blob = new Blob([csvContent], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `error_logs_${new Date().toISOString()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading logs:", error);
    }
  };

  /** Hide/Show Cards Logic **/
  const hideCard = (cardName) => {
    const updatedHiddenCards = [...hiddenCards, cardName];
    setHiddenCards(updatedHiddenCards);
    localStorage.setItem("hiddenCards", JSON.stringify(updatedHiddenCards));
  };

  const showAllCards = () => {
    setHiddenCards([]);
    localStorage.removeItem("hiddenCards");
  };

  const isCardHidden = (cardName) => hiddenCards.includes(cardName);

  /** Fetch User Login Trends **/
const fetchLoginTrends = async (selectedRange) => {
  console.log("Fetching login trends...");

  try {
    const orgId = sessionStorage.getItem("organizationId");
    if (!orgId) {
      console.warn("⚠️ No organization ID found.");
      return;
    }

    const loginTrendsQuery = query(
      collection(db, "loginTrends"),
      where("organizationId", "==", orgId),
      orderBy("timestamp", "desc")
    );

    const snapshot = await getDocs(loginTrendsQuery);
    const rawData = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Convert Firestore Timestamp to JavaScript Date
        timestamp: data.timestamp.toDate(),
      };
    });

    console.log("Raw Login Trends Data:", rawData);

    // Process the data to group by date & categorize logins
    const processedData = processLoginTrendsData(rawData);
    console.log("Processed Login Trends Data:", processedData);

    // Update state with aggregated data
    setLoginTrends(processedData);
  } catch (error) {
    console.error("Error fetching login trends:", error);
  }
};

const processLoginTrendsData = (data) => {
  const grouped = {};

  data.forEach((log) => {
    let date;
    
    // Convert timestamp to JavaScript Date, handling both Firestore Timestamps and already formatted Dates
    if (log.timestamp instanceof Date) {
      date = log.timestamp.toISOString().split('T')[0]; // Format YYYY-MM-DD
    } else if (log.timestamp && typeof log.timestamp.toDate === 'function') {
      date = log.timestamp.toDate().toISOString().split('T')[0]; // Convert Firestore Timestamp
    } else {
      console.warn("Invalid or missing timestamp in log:", log);
      return; // Skip processing if no valid timestamp
    }

    // Initialize date entry if not present
    if (!grouped[date]) {
      grouped[date] = { success: 0, failed: 0, admins: 0, users: 0 };
    }

    // Count successes and failures
    if (log.success) {
      grouped[date].success += 1;
    } else {
      grouped[date].failed += 1;
    }

    // Count admin and user logins
    if (log.role === "admin") {
      grouped[date].admins += 1;
    } else {
      grouped[date].users += 1;
    }
  });

  // Convert grouped object into an array of objects for Chart.js
  return Object.entries(grouped).map(([date, counts]) => ({
    date,
    success: counts.success,
    failed: counts.failed,
    admins: counts.admins,
    users: counts.users,
  }));
};

const loginChartData = {
  labels: loginTrends.map((log) => 
    log.date ? log.date : "N/A" // Use processed date from processLoginTrendsData
  ),
  datasets: [
    {
      label: "Successful Logins",
      data: loginTrends.map((log) => Number(log.success) || 0),
      backgroundColor: "rgba(54, 162, 235, 0.5)", // Blue
      borderColor: "rgba(54, 162, 235, 1)",
      borderWidth: 1,
    },
    {
      label: "Failed Logins",
      data: loginTrends.map((log) => Number(log.failed) || 0),
      backgroundColor: "rgba(255, 99, 132, 0.5)", // Red
      borderColor: "rgba(255, 99, 132, 1)",
      borderWidth: 1,
    },
    {
      label: "Admin Logins",
      data: loginTrends.map((log) => Number(log.admins) || 0),
      backgroundColor: "rgba(75, 192, 192, 0.5)", // Green
      borderColor: "rgba(75, 192, 192, 1)",
      borderWidth: 1,
    },
    {
      label: "User Logins",
      data: loginTrends.map((log) => Number(log.users) || 0),
      backgroundColor: "rgba(255, 206, 86, 0.5)", // Yellow
      borderColor: "rgba(255, 206, 86, 1)",
      borderWidth: 1,
    },
  ],
};
  const navigate = useNavigate();

  const isValidDate = (date) => {
    const parsedDate = new Date(date);
    return !isNaN(parsedDate);
  }; 

  /** Fetch MFA Status Summary from Firestore **/
const fetchMfaSummary = async () => {
  console.log("Fetching MFA status summary...");

  try {
    const orgId = sessionStorage.getItem("organizationId"); 
    if (!orgId) {
      console.warn("No organization ID found.");
      return;
    }

    const usersQuery = query(
      collection(db, "users"),
      where("organizationId", "==", orgId)
    );

    const snapshot = await getDocs(usersQuery);
    let mfaEnabledCount = 0;
    let mfaDisabledCount = 0;

    snapshot.docs.forEach((doc) => {
      if (doc.data().mfaEnabled) {
        mfaEnabledCount++;
      } else {
        mfaDisabledCount++;
      }
    });

    console.log("MFA Status:", { enabled: mfaEnabledCount, disabled: mfaDisabledCount });
    setMfaSummary({ enabled: mfaEnabledCount, disabled: mfaDisabledCount });
  } catch (error) {
    console.error("Error fetching MFA status:", error);
  }
};

  /** Fetch Active Sessions **/
const fetchActiveSessions = () => {
  const orgId = sessionStorage.getItem("organizationId");
  if (!orgId) return;

  console.log("Listening for real-time updates to active sessions...");

  // Listen for real-time changes in activeSessions collection
  const unsubscribe = onSnapshot(
    query(collection(db, "activeSessions"), where("organizationId", "==", orgId)),
    (snapshot) => {
      const activeSessionsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      console.log("Updated Active Sessions:", activeSessionsData);
      setActiveUsers(activeSessionsData); // Update the Active Users card
    },
    (error) => {
      console.error("Error listening for active sessions:", error);
    }
  );

  return unsubscribe; // Cleanup function to stop listening when component unmounts
};
  /** Ending a Users Session **/
const handleEndSession = async (session) => {
  try {
    const user = auth.currentUser;
    if (!user) return;

    const idToken = await user.getIdToken(true);

    await fetch("https://us-central1-integrity-resolutions-inc.cloudfunctions.net/revokeUserSession", {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
      // Pass both the user's UID and the specific session document ID
      body: JSON.stringify({ 
        userIdToRevoke: session.userId, 
        sessionIdToDelete: session.id 
      }),
    });

    // Optimistically remove the session from the UI
    setActiveUsers(prev => prev.filter(s => s.id !== session.id));

  } catch (error) {
    console.error("Error ending session:", error);
  }
};

  
  return (
    <>
      {loading ? (
        // Show Kinova icon as spinner while loading
        <div className="loading-container">
          <img 
            src="https://storage.googleapis.com/kinova/Kinova_lcon_300px.png"
            alt="Loading..."
            className="loading-spinner"
          />
        </div>
      ) : (
        <div className="dashboard-container">
          <Sidebar />
          <div className="dashboard-content">
            <h1 className="dashboard-header">Admin Dashboard</h1>
            <div className="dashboard-grid">
              {/* === All Cards Here === */}
            
          {/* Total Users */}
          {!isCardHidden("Total Users") && (
            <Card className="dashboard-card half-width">
              <CardContent>
                <div className="card-header">
                  <h2>Total Users</h2>
                  <div>
                    {/* Refresh Button */}
                  <IconButton size="small" onClick={handleRefreshTotalUsers} color="primary">
                  <RefreshIcon />
                </IconButton>

                    {/* Hide Button */}
                  <IconButton size="small" onClick={() => hideCard("Total Users")}>
                  <CloseIcon />
                  </IconButton>
                  </div>
                </div>
                <p>{users.length} users</p>

                {/* Table for Users */}
                <TableContainer style={{ maxHeight: 300, overflow: 'auto' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>User Email</strong></TableCell>
                        <TableCell><strong>Last Login</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            {isValidDate(user.lastLogin)
                              ? new Date(user.lastLogin).toLocaleString()
                              : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Manage Users Button */}
                <div style={{ marginTop: '16px' }}>
                  <Button variant="contained" color="primary" onClick={() => navigate('./manage-users')}>
                    Manage Users
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pending Verifications */}
          {!isCardHidden("Pending Verifications") && (
            <Card className="dashboard-card half-width">
              <CardContent>
                <div className="card-header">
                  <h2>Pending Verifications</h2>
                  <div>
                    {/* Refresh Button */}
                    <IconButton size="small" onClick={handleRefreshPendingVerifications} color="primary">
                      <RefreshIcon />
                    </IconButton>

                    {/* Hide Button */}
                    <IconButton size="small" onClick={() => hideCard("Pending Verifications")}>
                      <CloseIcon />
                    </IconButton>
                  </div>
                </div>
                <p>{pendingVerifications.length} pending</p>

                {pendingVerifications.length > 0 ? (
                  <TableContainer style={{ maxHeight: 300, overflow: 'auto' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>User Email</strong></TableCell>
                          <TableCell><strong>Action</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pendingVerifications.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                              <ResendVerificationButton email={user.email} />
                            </TableCell>
                            <TableCell>{resendStatus[user.email] || "Pending"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <p>All users are verified.</p>
                )}
              </CardContent>
            </Card>
          )}
  
          {/* Recently Created Users */}
          {!isCardHidden("Recently Created Users") && (
                <Card className="dashboard-card half-width">
                <CardContent>
                  <div className="card-header">
                    <h2>Recently Created Users</h2>
                    <div>
                    {/* Refresh Button */}
                  <IconButton size="small" onClick={handleRefreshRecentlyCreatedUsers} color="primary">
                  <RefreshIcon />
                </IconButton>

                    {/* Hide Button */}
                  <IconButton size="small" onClick={() => hideCard("Recently Created Users")}>
                  <CloseIcon />
                  </IconButton>
                  </div>
                  </div>
                  <p>{recentlyCreatedUsers.length} users</p>
          
                  <TableContainer style={{ maxHeight: 300, overflow: 'auto' }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>User Email</strong></TableCell>
                          <TableCell><strong>Created Date</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recentlyCreatedUsers.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                              {user.createdDate
                                ? new Date(user.createdDate).toLocaleString()
                                : 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

          {/* Disabled Accounts */}
          {!isCardHidden("Disabled Accounts") && (
                <Card className="dashboard-card half-width">
                <CardContent>
                  <div className="card-header">
                    <h2>Disabled Accounts</h2>
                    <div>
                    {/* Refresh Button */}
                  <IconButton size="small" onClick={handleRefreshDisabledAccounts} color="primary">
                  <RefreshIcon />
                </IconButton>

                    {/* Hide Button */}
                  <IconButton size="small" onClick={() => hideCard("Disabled Accounts")}>
                  <CloseIcon />
                  </IconButton>
                  </div>
                  </div>
                  <p>{disabledAccounts.length} users</p>
          
                  <TableContainer style={{ maxHeight: 300, overflow: 'auto' }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>User Email</strong></TableCell>
                          <TableCell><strong>Disabled Date</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {disabledAccounts.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                              {user.disabledAt
                                ? new Date(user.disabledAt).toLocaleString()
                                : 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

          {/* Active Users */}
          {!isCardHidden("Active Users") && (
            <Card className="dashboard-card half-width">
             <CardContent>
               <div className="card-header">
                 <h2>Active Users</h2>
                    {/* Hide Button */}
                  <IconButton size="small" onClick={() => hideCard("Active Users")}>
                  <CloseIcon />
                  </IconButton>
               </div>
               <p>{activeUsers.length} users currently online</p>
       
               <TableContainer style={{ maxHeight: 300, overflow: 'auto' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>User Email</strong></TableCell>
                      <TableCell><strong>Login Time</strong></TableCell>
                      <TableCell><strong>Device ID</strong></TableCell>
                      <TableCell><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeUsers.map((session) => (
                      <TableRow key={session.id}>
                        <TableCell>{session.email}</TableCell>
                        <TableCell>
                          {session.loginTime
                            ? new Date(session.loginTime.seconds * 1000).toLocaleString()
                            : 'N/A'}
                        </TableCell>
                        <TableCell>{session.deviceId || "Unknown Device"}</TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            onClick={() => handleEndSession(session.id)}
                          >
                            End Session
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
             </CardContent>
           </Card>
          )}

          {/* Users with Failed Logins */}
          {!isCardHidden("Users with Failed Logins") && (
            <Card className="dashboard-card half-width">
              <CardContent>
                <div className="card-header">
                  <h2>Users With Failed Logins</h2>
                   {/* Hide Button */}
                   <IconButton size="small" onClick={() => hideCard("Users with Failed Logins")}>
                  <CloseIcon />
                  </IconButton>
                </div>
                <p>{failedLogins.length} failed login attempts</p>

                <TableContainer style={{ maxHeight: 300, overflow: "auto" }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>User Email</strong></TableCell>
                        <TableCell><strong>Failed Attempt Time</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {failedLogins.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            {user.timestamp
                              ? new Date(user.timestamp.seconds * 1000).toLocaleString()
                              : "N/A"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* User Login Trends (Chart.js Graph) */}
          {!isCardHidden("User Login Trends") && (
            <Card className="dashboard-card full-width">
              <CardContent>
                <div className="card-header">
                  <h2>User Login Trends (Last 10 Logins)</h2>
                  <div>
                  {/* Refresh Button */}
                  <IconButton onClick={handleRefreshLoginTrends} color="primary">
                    <RefreshIcon />
                  </IconButton>

                  {/* Hide Button */}
                  <IconButton size="small" onClick={() => hideCard("User Login Trends")}>
                  <CloseIcon />
                  </IconButton>
                  </div>
                </div>

                {/* Styled Dropdown using Material UI */}
                <FormControl size="small" style={{ width: "25%", marginBottom: "10px", minWidth: 200 }}>
                  <InputLabel>Time Range</InputLabel>
                  <Select
                    value={selectedRange}
                    onChange={(e) => {
                      setSelectedRange(e.target.value);
                      fetchLoginTrends(e.target.value); // Fetch new data on change
                    }}
                    label="Time Range"
                  >
                    <MenuItem value="last7days">Last 7 Days</MenuItem>
                    <MenuItem value="last30days">Last 30 Days</MenuItem>
                  </Select>
                </FormControl>

                {/* Constrain Chart Size */}
                <div style={{ height: "300px" }}>
                  {loginTrends.length > 0 ? (
                    <Bar
                      data={loginChartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          x: {
                            title: {
                              display: true,
                              text: "Date",
                            },
                            ticks: {
                              autoSkip: true,
                              maxTicksLimit: 10, // Prevent too many labels
                            },
                          },
                          y: {
                            title: {
                              display: true,
                              text: "Logins",
                            },
                            beginAtZero: true,
                            ticks: {
                              stepSize: 1,
                              precision: 0,
                            },
                          },
                        },
                        plugins: {
                          legend: {
                            position: "top",
                          },
                        },
                      }}
                    />
                  ) : (
                    <p>No recent login trends found.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

         {/* Recent Errors */}
         {!isCardHidden("Recent Errors") && (
        <Card className="dashboard-card half-width">
          <CardContent>
            <div className="card-header">
              <h2>Recent Errors</h2>
              <div>
                {/* Refresh Button */}
                <IconButton onClick={handleRefreshRecentErrors} color="primary">
                    <RefreshIcon />
                  </IconButton>

                  {/* Hide Button */}
              <IconButton size="small"  onClick={() => hideCard("Recent Errors")}>
                <CloseIcon />
              </IconButton>
              </div>
            </div>

            {/* Real-Time Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={enableRealtimeErrors}
                  onChange={() => setEnableRealtimeErrors((prevState) => !prevState)}
                  color="primary"
                />
              }
              label={enableRealtimeErrors ? "Real-Time Updates: ON" : "Real-Time Updates: OFF"}
            />

            {/* List of Errors */}
            <ul className="error-list">
              {recentErrors.length > 0 ? (
                recentErrors.map((error, index) => (
                  <li key={index} className="error-entry">
                    <p><strong>Error Message:</strong> {error.message}</p>
                    <p><strong>Timestamp:</strong> {new Date(error.timestamp.seconds * 1000).toLocaleString()}</p>
                  </li>
                ))
              ) : (
                <p>No recent errors found.</p>
              )}
            </ul>

                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleDownloadErrors}
                  style={{ marginTop: "10px" }}
                >
                  Download Error Logs
                </Button>
              </CardContent>
            </Card>
          )}
          
          {/* Recent User Imports (Polished & Scrollable) */}
          {!isCardHidden("Recent User Imports") && (
            <Card className="dashboard-card full-width">
              <CardContent>
                <div className="card-header">
                  <h2>Recent User Imports</h2>
                  <div>
                    {/* Refresh Button */}
                  <IconButton size="small" onClick={handleRefreshRecentUserImports} color="primary">
                  <RefreshIcon />
                </IconButton>

                    {/* Hide Button */}
                  <IconButton size="small" onClick={() => hideCard("Recent User Imports")}>
                    <CloseIcon />
                  </IconButton>
                  </div>
                </div>

                {recentImports.length > 0 ? (
                  <TableContainer style={{ maxHeight: 300, overflow: "auto" }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Date</strong></TableCell>
                          <TableCell><strong>Successful</strong></TableCell>
                          <TableCell><strong>Failed</strong></TableCell>
                          <TableCell><strong>Details</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recentImports.map((importLog, index) => (
                          <TableRow key={index}>
                            <TableCell>{importLog.timestamp?.toDate().toLocaleString() || "N/A"}</TableCell>
                            <TableCell>{Array.isArray(importLog.successfulImports) ? importLog.successfulImports.length : 0}</TableCell>
                            <TableCell>{Array.isArray(importLog.failedImports) ? importLog.failedImports.length : 0}</TableCell>
                            <TableCell>
                              <Accordion>
                                <AccordionSummary 
                                expandIcon={<ExpandMoreIcon />}className="accordion-summary">
                                  <Typography variant="body2" color="primary">View Details</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Typography variant="body2">
                                    <strong>Failed Imports:</strong>
                                  </Typography>
                                  <ul>
                                    {Array.isArray(importLog.failedImports) && importLog.failedImports.map((fail, i) => (
                                      <li key={i}>
                                        <strong>Email:</strong> {fail.email} <br />
                                        <strong>Error:</strong> {fail.error}
                                      </li>
                                    ))}
                                  </ul>
                                  <Typography variant="body2">
                                    <strong>Successful Imports:</strong>
                                  </Typography>
                                  <ul>
                                    {Array.isArray(importLog.successfulImports) && importLog.successfulImports.map((success, i) => (
                                      <li key={i}>
                                        <strong>Email:</strong> {success.email} <br />
                                        <strong>User ID:</strong> {success.userId} <br />
                                        <strong>Email Verified:</strong> {success.emailVerified ? "Yes" : "No"} <br />
                                        <strong>Message:</strong> {success.message}
                                      </li>
                                    ))}
                                  </ul>
                                </AccordionDetails>
                              </Accordion>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="textSecondary">No recent imports found.</Typography>
                )}
              </CardContent>
            </Card>
          )}

          {/* Additional Cards */}
          {/* Recent Admin Actions */}
          {!isCardHidden("Recent Admin Actions") && (
            <Card className="dashboard-card half-width">
              <CardContent>
                <div className="card-header">
                  <h2>Recent Admin Actions</h2>
                  <div>
                  {/* Refresh Button */}
                  <IconButton onClick={fetchAdminActions} color="primary" title="Refresh Actions">
                    <RefreshIcon />
                  </IconButton>

                  {/* Hide Button */}
              <IconButton size="small"  onClick={() => hideCard("Recent Errors")}>
                <CloseIcon />
              </IconButton>
              </div>
                </div>

                {/* Real-Time Toggle */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={enableRealtimeAdminActions}
                      onChange={() => setEnableRealtimeAdminActions((prev) => !prev)}
                      color="primary"
                    />
                  }
                  label="Real-Time Updates"
                />

                <TableContainer style={{ maxHeight: 300, overflow: 'auto' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Admin</strong></TableCell>
                        <TableCell><strong>Action</strong></TableCell>
                        <TableCell><strong>Timestamp</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {adminActions.length > 0 ? (
                        adminActions.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell>{log.adminEmail}</TableCell>
                            <TableCell>{log.action}</TableCell>
                            <TableCell>
                              {log.timestamp?.seconds 
                                ? new Date(log.timestamp.seconds * 1000).toLocaleString() 
                                : "N/A"}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} style={{ textAlign: "center" }}>
                            No recent admin actions found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* MFA Status Summary */}
          {!isCardHidden("MFA Status Summary") && (
            <Card className="dashboard-card half-width">
              <CardContent>
                <div className="card-header">
                  <h2>MFA Status Summary</h2>
                  <div>
                    {/* Refresh Button */}
                  <IconButton onClick={fetchMfaSummary} color="primary" title="Refresh MFA Summary">
                    <RefreshIcon />
                  </IconButton>

                  {/* Hide Button */}
                  <IconButton size="small"  onClick={() => hideCard("Recent Errors")}>
                    <CloseIcon />
                  </IconButton>
                  </div>
                </div>

                {/* Real-Time Toggle */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={enableRealtimeMfa}
                      onChange={() => setEnableRealtimeMfa((prev) => !prev)}
                      color="primary"
                    />
                  }
                  label="Real-Time Updates"
                />

                <TableContainer style={{ maxHeight: 300, overflow: 'auto' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Count</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Enabled</TableCell>
                        <TableCell>{mfaSummary.enabled}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Disabled</TableCell>
                        <TableCell>{mfaSummary.disabled}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
         </div>
          {/* Show All Hidden Cards */}
          <div className="show-all-container">
            <button className="show-all-btn" onClick={showAllCards}>Show All Cards</button>
          </div>
        </div>
      </div>
    )}
  </>
  )};
